import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Whether the kiosk has paused new orders, as the fan page should treat it.
 *
 * The page's own copy of the serving kiosk's pause switch (operator_settings
 * overlaid on the settings; club_settings only when no kiosk serves the seat)
 * arrives with the initial load and then only through a realtime push. At KTP vs MP (25 Sep 2026) staff
 * paused ordering mid-match, the push never reached a fan who had loaded the
 * page earlier, and they pressed an enabled "Pay Now" 23 times against the
 * server's 409 while a toast told them "back shortly". So the page now also:
 *  - locks the moment the server refuses an order as paused;
 *  - re-reads the switch every time the ordering view is opened;
 *  - unlocks only on a fresh read (or a realtime flip) that says open.
 */
export interface UseOrderingPauseOptions {
  /** The pause switch as the page currently knows it. */
  settingsPaused: boolean;
  /** True while the ordering view is open; each opening re-reads the switch. */
  orderingOpen: boolean;
  /** Fresh read of the serving kiosk's switch (readOrderingPaused); null when it cannot answer. */
  fetchPaused: () => Promise<boolean | null>;
  /** A fresh value, so the caller can merge it into its live settings copy. */
  onFreshValue?: (paused: boolean) => void;
}

export function useOrderingPause({ settingsPaused, orderingOpen, fetchPaused, onFreshValue }: UseOrderingPauseOptions) {
  // What the server last told us, independent of the (possibly stale) settings copy.
  const [serverPaused, setServerPaused] = useState(false);

  // Callbacks live in refs so the effects below never re-run because a caller
  // re-created a closure on render.
  const fetchRef = useRef(fetchPaused);
  fetchRef.current = fetchPaused;
  const freshRef = useRef(onFreshValue);
  freshRef.current = onFreshValue;

  const recheck = useCallback(async () => {
    const fresh = await fetchRef.current();
    if (fresh === null) return; // no row / error: keep what we know
    setServerPaused(fresh);
    freshRef.current?.(fresh);
  }, []);

  // Opening the ordering view asks the server again, every time.
  useEffect(() => {
    if (orderingOpen) void recheck();
  }, [orderingOpen, recheck]);

  // A realtime (or refetched) flip from paused to open clears the override.
  const prevSettingsPaused = useRef(settingsPaused);
  useEffect(() => {
    if (prevSettingsPaused.current && !settingsPaused) setServerPaused(false);
    prevSettingsPaused.current = settingsPaused;
  }, [settingsPaused]);

  // The server refused an order as paused: lock now, then confirm with a read.
  const markPausedByServer = useCallback(() => {
    setServerPaused(true);
    void recheck();
  }, [recheck]);

  return { paused: settingsPaused || serverPaused, markPausedByServer, recheck };
}
